import {
  domainCodeAtom,
  DomainCodeDialog,
  getValidBusinessRoles,
  NavBarLink,
  useSaladBar,
  useValidDomains,
} from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { List, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useAtom, useAtomValue } from 'jotai';
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';

import { clearUserPermCache } from '@/apis/users';
import { NotRenewingContext } from '@/App';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';
import { useUserDetails } from '@/hooks';
import { BUSINESS_ROLE_MAPPING, extractRelevantDomainCodes } from '@/util';
import GitCommitDisplay from '@components/GitCommitDisplay';
import PreferencesDialog from '@components/PreferencesDialog';
import { useSentryTags } from '@hooks/useSentryTags';
import { ChatBubble, Logout, School, Settings } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

const VALID_BUSINESS_ROLES = getValidBusinessRoles(BUSINESS_ROLE_MAPPING);

const useInitializeDomainCode = (setIsDomainCodeDialogOpen: Dispatch<SetStateAction<boolean>>) => {
  const [domainCode, setDomainCode] = useAtom(domainCodeAtom);
  const { data: userDetails, isLoading, isError: isUserDetailsError } = useUserDetails();
  const isBusinessRoleAuthorisationToggleEnabled = useFeatureToggle('Business Role Authorisation');
  const {
    data: validDomains,
    isLoading: isValidDomainsLoading,
    isError: isValidDomainsError,
  } = useValidDomains(VALID_BUSINESS_ROLES);

  const availableDomains = isBusinessRoleAuthorisationToggleEnabled
    ? validDomains
    : userDetails
      ? extractRelevantDomainCodes(userDetails)
      : undefined;

  const domainsLoading = isBusinessRoleAuthorisationToggleEnabled
    ? isValidDomainsLoading
    : isLoading;
  const domainsError = isBusinessRoleAuthorisationToggleEnabled
    ? isValidDomainsError
    : isUserDetailsError;

  useSentryTags({ organisation: domainCode });

  useEffect(() => {
    if (
      availableDomains !== undefined &&
      (domainCode === '' || !availableDomains.includes(domainCode))
    ) {
      if (availableDomains.length === 1) {
        setDomainCode(availableDomains[0]);
      } else setIsDomainCodeDialogOpen(true);
    }
  }, [availableDomains, domainCode, setDomainCode, setIsDomainCodeDialogOpen]);

  return { availableDomains, isLoading: domainsLoading, isError: domainsError };
};

const channel = new BroadcastChannel('logout');

const useClearUserCache = () => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);
  const { enqueueSuccessNotification } = useSaladBar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      await clearUserPermCache(token, domainCode);
      queryClient.invalidateQueries({ queryKey: ['precacheusercontactperms'] });
    },
    onSuccess: () => {
      enqueueSuccessNotification('Cache successfully cleared');
    },
  });
};

function isWorthyToCheckForNotRenewing(pathnameStr: string) {
  return (
    pathnameStr.startsWith('/quicksearch') ||
    pathnameStr.startsWith('/advancedsearch') ||
    pathnameStr.startsWith('/template')
  );
}

export default function NavBarBottomContent() {
  const { logout, getAccessTokenSilently } = useAuth0();
  const [isPreferencesDialogOpen, setIsPreferencesDialogOpen] = useState(false);
  const [isDomainCodeDialogOpen, setIsDomainCodeDialogOpen] = useState(false);

  const { availableDomains, isLoading, isError } =
    useInitializeDomainCode(setIsDomainCodeDialogOpen);
  const { mutate: clearUserCache } = useClearUserCache();

  const { notRenewingPresent } = useContext(NotRenewingContext);

  // See https://github.com/getsentry/sentry-javascript/issues/10941
  const feedback = Sentry.getFeedback();

  const handleLogout = useCallback(async () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);
  const devToolsEnabled = useFeatureToggle('Developer Features');
  const { data: userDetails } = useUserDetails();

  const settingsSubMenu: { label: string; onClick: () => void }[] = [];
  settingsSubMenu.push({
    label: 'Preferences',
    onClick: () => setIsPreferencesDialogOpen(true),
  });
  settingsSubMenu.push({
    label: 'Select organisation',
    onClick: () => setIsDomainCodeDialogOpen(true),
  });

  settingsSubMenu.push({
    label: 'Clear user cache',
    onClick: clearUserCache,
  });

  // For LIST-1163, we make sure that the "Not Renewing" information is removed
  // from local storage

  useEffect(() => {
    const legacy = localStorage.getItem('showNotRenewingField');
    if (legacy !== null) {
      localStorage.removeItem('showNotRenewingField');
    }
  }, []);

  useEffect(() => {
    channel.onmessage = (e) => {
      if (e.data === 'logout') setTimeout(() => window.location.reload(), 500);
    };
  }, [getAccessTokenSilently]);

  // We only need to check if there is a notRenewing field set on three types of
  // pages - Quick, Advanced and Template pages.

  const { pathname } = useLocation();

  return (
    <>
      {devToolsEnabled && (
        <>
          <Typography
            sx={{
              textAlign: 'center',
              marginY: '0.5rem',
              whiteSpace: 'wrap',
            }}
          >
            You are a <br />
            <strong>{userDetails?.userLevel}</strong> <br />
            user
          </Typography>
          <GitCommitDisplay wide={true} />
        </>
      )}
      <List disablePadding sx={{ width: '100%' }}>
        <NavBarLink
          label="Training Hub"
          icon={<School />}
          to="https://training.greens.org.au/courses/list-manager/"
          openInNewWindow
          tooltipMenuClosed="Training Hub - List Manager user guide"
          tooltipMenuOpen="List Manager user guide"
        />
        {feedback && (
          <NavBarLink
            label="Send feedback"
            icon={<ChatBubble />}
            onClick={async () => {
              if (feedback) {
                const form = await feedback.createForm();
                form.appendToDom();
                form.open();
              }
            }}
            tooltipMenuClosed="Send feedback"
          />
        )}
        <NavBarLink
          label="Settings"
          icon={<Settings />}
          subMenu={settingsSubMenu}
          tooltipMenuClosed="Settings"
          extraSubIndentSpace={3}
        />
        <NavBarLink
          label="Log out"
          icon={<Logout />}
          onClick={() => {
            channel.postMessage('logout');
            handleLogout();
          }}
          tooltipMenuClosed="Log out"
        />
      </List>

      <PreferencesDialog
        open={isPreferencesDialogOpen}
        closeHandler={() => setIsPreferencesDialogOpen(false)}
        disableNotRenewing={isWorthyToCheckForNotRenewing(pathname) && notRenewingPresent}
      />

      <DomainCodeDialog
        isLoading={isLoading}
        isError={isError}
        isOpen={isDomainCodeDialogOpen}
        key={isDomainCodeDialogOpen ? 'a' : 'b'}
        onClose={() => setIsDomainCodeDialogOpen(false)}
        domainOptions={availableDomains ?? []}
        applicationName={'List Manager'}
        handleLogout={() => {
          channel.postMessage('logout');
          handleLogout();
        }}
      />
    </>
  );
}
